import React, { useState, useEffect } from 'react';
import { Calendar, ExternalLink, Heart, Play, Bell } from 'lucide-react';
import { toast } from 'sonner';
import TrailerModal from './TrailerModal';
import DetailModal from './DetailModal';
import { useSubscription } from '../context/SubscriptionContext';

const PLATFORM_COLORS = {
  'Netflix': 'bg-[#E50914]',
  'Prime Video': 'bg-[#00A8E1]',
  'Apple TV+': 'bg-black',
  'Hulu': 'bg-[#1CE783]',
  'Disney+': 'bg-[#113CCF]',
  'HBO Max': 'bg-[#9D34DA]'
};

const ReleaseCard = ({ release }) => {
  const [isInWatchlist, setIsInWatchlist] = useState(false);
  const [showTrailerModal, setShowTrailerModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [notifyRequested, setNotifyRequested] = useState(false);
  const { requireSubscription, notifyOnRelease } = useSubscription();

  useEffect(() => {
    const watchlist = JSON.parse(localStorage.getItem('watchlist') || '[]');
    setIsInWatchlist(watchlist.some(item => item.id === release.id));
    const notified = JSON.parse(localStorage.getItem('notify_releases') || '[]');
    setNotifyRequested(notified.includes(release.id));
  }, [release.id]);

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const isUpcoming = () => {
    return new Date(release.release_date) > new Date();
  };

  const toggleWatchlist = (e) => {
    e.preventDefault();
    e.stopPropagation();

    requireSubscription(() => {
      const watchlist = JSON.parse(localStorage.getItem('watchlist') || '[]');
      const alreadyIn = watchlist.some(item => item.id === release.id);

      if (alreadyIn) {
        const updated = watchlist.filter(item => item.id !== release.id);
        localStorage.setItem('watchlist', JSON.stringify(updated));
        setIsInWatchlist(false);
      } else {
        watchlist.push(release);
        localStorage.setItem('watchlist', JSON.stringify(watchlist));
        setIsInWatchlist(true);
      }

      // Dispatch event for other components to listen
      window.dispatchEvent(new Event('watchlistUpdated'));
    }, 'watchlist');
  };

  const handleNotifyClick = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const doNotify = async () => {
      try {
        // Read fresh email from storage to avoid stale closure when this
        // callback fires from inside the post-subscription resume flow.
        const latestEmail = localStorage.getItem('tmj_subscribed_email');
        const result = await notifyOnRelease(release, latestEmail);
        const notified = JSON.parse(localStorage.getItem('notify_releases') || '[]');
        if (!notified.includes(release.id)) {
          notified.push(release.id);
          localStorage.setItem('notify_releases', JSON.stringify(notified));
        }
        setNotifyRequested(true);
        // SEC-002 double-opt-in — backend returns pending_verification and
        // ships a confirm email. Show a longer-duration toast so the user
        // knows to check their inbox before the reminder is real.
        if (result && result.status === 'pending_verification') {
          toast.success(
            `Check your inbox to confirm the reminder for "${release.title}"`,
            { duration: 6000 }
          );
        } else {
          toast.success(`We'll email you when "${release.title}" drops`);
        }
      } catch (err) {
        toast.error('Could not set reminder. Please try again.');
      }
    };

    requireSubscription(doNotify, 'notify');
  };

  return (
    <div 
      data-testid={`release-card-${release.id}`}
      className="group relative aspect-[2/3] overflow-hidden rounded-xl bg-gray-900 border border-white/5 transition-all duration-500 hover:border-white/20 hover:shadow-[0_0_30px_rgba(255,255,255,0.1)] animate-fade-in cursor-pointer"
      onClick={() => setShowDetailModal(true)}
    >
      {/* Poster Image */}
      <img
        src={release.poster_url}
        alt={release.title}
        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        loading="lazy"
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent opacity-80 transition-opacity group-hover:opacity-95" />
      
      {/* Watchlist + Notify Buttons - Top Right */}
      <div className="absolute top-4 right-4 z-20 flex flex-col gap-2 items-end">
        <button
          onClick={toggleWatchlist}
          data-testid={`watchlist-btn-${release.id}`}
          className="p-2 rounded-full bg-black/70 backdrop-blur-sm border border-white/20 transition-all hover:scale-110 active:scale-95"
          aria-label="Add to watchlist"
        >
          <Heart
            className={`w-5 h-5 ${isInWatchlist ? 'fill-red-500 text-red-500' : 'text-white'}`}
          />
        </button>
        {isUpcoming() && (
          <button
            onClick={handleNotifyClick}
            data-testid={`notify-btn-${release.id}`}
            title={notifyRequested ? 'Reminder set' : 'Notify me on release'}
            aria-label={notifyRequested ? 'Reminder set' : 'Notify me on release'}
            className={`p-2 rounded-full backdrop-blur-sm border transition-all hover:scale-110 active:scale-95 ${
              notifyRequested
                ? 'bg-amber-500/90 border-amber-300 text-black'
                : 'bg-black/70 border-white/20 text-white'
            }`}
          >
            <Bell
              className={`w-5 h-5 ${notifyRequested ? 'fill-black' : ''}`}
            />
          </button>
        )}
      </div>
      
      {/* Content */}
      <div className="absolute inset-0 p-4 flex flex-col">
        {/* Top badges - Stack vertically with proper spacing */}
        <div className="flex flex-col gap-2 pr-14 max-w-[calc(100%-4rem)] mb-auto">
          <span 
            data-testid={`platform-badge-${release.platform.toLowerCase().replace(/\s+/g, '-')}`}
            className={`${PLATFORM_COLORS[release.platform] || 'bg-gray-600'} text-white text-xs font-mono uppercase px-2 py-1 rounded flex-shrink-0 w-fit`}
          >
            {release.platform}
          </span>
          <span className="bg-black/50 backdrop-blur-sm text-white text-xs font-mono uppercase px-2 py-1 rounded border border-white/10 flex-shrink-0 w-fit">
            {release.type}
          </span>
        </div>

        {/* Bottom content - fixed at bottom */}
        <div className="space-y-2 mt-auto">
          <h3 className="font-primary text-xl md:text-2xl text-white leading-tight uppercase tracking-wide line-clamp-2">
            {release.title}
          </h3>
          
          <div className="flex items-center gap-2 text-gray-300">
            <Calendar className="w-4 h-4 flex-shrink-0" />
            <span className="font-mono text-xs uppercase tracking-wider truncate">
              {formatDate(release.release_date)}
            </span>
            {release.language && (
              <span className="ml-auto text-xs text-gray-400 uppercase font-mono flex-shrink-0">
                {release.language}
              </span>
            )}
          </div>
          
          {/* Action buttons - always visible on mobile, hover on desktop */}
          <div className="flex gap-2 items-center md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-300">
            {release.trailer_url ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowTrailerModal(true);
                }}
                data-testid={`trailer-button-${release.id}`}
                title="Watch trailer"
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-red-600 text-white hover:bg-red-700 font-secondary font-bold py-1 px-2.5 rounded-full transition-all text-xs cursor-pointer"
              >
                <Play className="w-3 h-3 flex-shrink-0" />
                <span className="truncate">Trailer</span>
              </button>
            ) : release.streaming_url && !isUpcoming() ? (
              <a
                href={release.streaming_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`watch-button-${release.id}`}
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-white text-black hover:bg-gray-200 font-secondary font-bold py-1 px-2.5 rounded-full transition-all text-xs"
              >
                <span className="truncate">Watch on {release.platform}</span>
                <ExternalLink className="w-3 h-3 flex-shrink-0" />
              </a>
            ) : null}

            {/* Coming Soon pill — sits inline next to Trailer so neither
                button crowds the poster. */}
            {isUpcoming() && (
              <span
                data-testid={`coming-soon-${release.id}`}
                className="bg-green-500 text-black text-[10px] font-bold px-2 py-1 rounded-full uppercase font-mono tracking-wide flex-shrink-0"
              >
                Soon
              </span>
            )}
          </div>
        </div>
      </div>
      
      {/* Trailer Modal */}
      {showTrailerModal && release.trailer_url && (
        <TrailerModal
          trailerUrl={release.trailer_url}
          title={release.title}
          onClose={() => setShowTrailerModal(false)}
        />
      )}
      
      {/* Detail Modal */}
      {showDetailModal && (
        <DetailModal
          release={release}
          isInWatchlist={isInWatchlist}
          onToggleWatchlist={toggleWatchlist}
          onClose={() => setShowDetailModal(false)}
        />
      )}
    </div>
  );
};

export default ReleaseCard;
